import React, { useEffect, useCallback, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Chip from "@mui/material/Chip";
import L from "leaflet";
import "@google/model-viewer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import {
  startAttemptByCodeCall,
  getAttemptCall,
  submitAttemptCall,
  getAttemptResultCall,
  listMyAttemptsCall,
} from "../../redux/api/quizSystem.api";

const formatTime = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

const STATUS_LABELS = {
  in_progress: "Active",
  submitted: "Completed",
  expired: "Expired",
};

const looksLike3DUrl = (url) => {
  if (!url || typeof url !== "string") return false;
  const path = url.split("?")[0].toLowerCase();
  return path.endsWith(".glb") || path.endsWith(".gltf");
};

const createSlidePinIcon = (label) =>
  L.divIcon({
    className: "slide-pin-marker-wrapper",
    html: `<div style="display:flex;flex-direction:column;align-items:center;line-height:1;pointer-events:none;">
      <div style="width:0;height:0;border-left:8px solid transparent;border-right:8px solid transparent;border-top:14px solid #28a745;"></div>
      <div style="background:#007bff;color:white;font-weight:bold;font-size:13px;border-radius:4px;padding:3px 8px;margin-top:2px;min-width:22px;text-align:center;box-sizing:border-box;">${label}</div>
    </div>`,
    iconSize: [24, 44],
    iconAnchor: [12, 0],
  });

export default function QuizByCode() {
  const [accessCode, setAccessCode] = useState("");
  const [attemptPreview, setAttemptPreview] = useState(null);
  const [currentAttempt, setCurrentAttempt] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [pinPositions, setPinPositions] = useState({});
  const [timeLeft, setTimeLeft] = useState(null);
  const [result, setResult] = useState(null);
  const [resultDetails, setResultDetails] = useState(null);
  const [resultDialog, setResultDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pastAttempts, setPastAttempts] = useState([]);
  const answersRef = useRef({});
  const pinPositionsRef = useRef({});
  const slideMapRef = useRef(null);
  const slideAnswerMarkerRef = useRef(null);
  answersRef.current = answers;
  pinPositionsRef.current = pinPositions;

  const loadPastAttempts = useCallback(async () => {
    const res = await listMyAttemptsCall();
    const data = res?.data;
    setPastAttempts(Array.isArray(data) ? data : []);
  }, []);

  useEffect(() => {
    loadPastAttempts();
  }, [loadPastAttempts]);

  const handleAccessQuiz = async () => {
    const code = (accessCode || "").trim().toUpperCase();
    if (!code) {
      setError("Please enter a quiz code.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const res = await startAttemptByCodeCall(code);
      const status = res?.status;
      const data = res?.data;
      if ((status === 200 || status === 201) && data?.id) {
        setAttemptPreview(data);
        setCurrentAttempt(null);
      } else {
        setError(res?.message || "Invalid or expired code.");
        setAttemptPreview(null);
      }
    } catch (e) {
      setError("Could not access quiz.");
      setAttemptPreview(null);
    }
    setLoading(false);
  };

  const startOrContinueQuiz = () => {
    if (!attemptPreview?.id) return;
    setCurrentAttempt(attemptPreview);
    setAttemptPreview(null);
    setCurrentQuestionIndex(0);
    setAnswers({});
    setPinPositions({});
    const limitSec = (attemptPreview.time_limit_minutes || 30) * 60;
    const started = new Date(attemptPreview.started_at).getTime();
    const elapsed = Math.floor((Date.now() - started) / 1000);
    setTimeLeft(Math.max(0, limitSec - elapsed));
    setResult(null);
    setResultDialog(false);
  };

  const submitCurrentAttempt = useCallback(async () => {
    if (!currentAttempt?.id || loading) return;
    setLoading(true);
    const answerList = Object.entries(answersRef.current).map(([question_bank_id, selected_answer_key]) => {
      const pinPos = pinPositionsRef.current[question_bank_id];
      const payload = { question_bank_id, selected_answer_key };
      if (pinPos && typeof pinPos.x === "number" && typeof pinPos.y === "number") {
        payload.pin_position = { x: pinPos.x, y: pinPos.y };
      }
      return payload;
    });
    try {
      const res = await submitAttemptCall(currentAttempt.id, answerList);
      const data = res?.data;
      if (res?.status === 200 && data) {
        setResult(data);
        setResultDialog(true);
        setCurrentAttempt(null);
        setTimeLeft(null);
        loadPastAttempts();
        if (data.id) {
          const detailRes = await getAttemptResultCall(data.id);
          const detailData = detailRes?.data;
          if (detailData) setResultDetails(detailData);
        }
      }
    } catch (e) {
      setError("Failed to submit quiz.");
    }
    setLoading(false);
  }, [currentAttempt?.id, loadPastAttempts]);

  useEffect(() => {
    if (!currentAttempt || timeLeft === null || timeLeft <= 0) return;
    const t = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(t);
          setLoading(true);
          const answerList = Object.entries(answersRef.current).map(([question_bank_id, selected_answer_key]) => {
            const pinPos = pinPositionsRef.current[question_bank_id];
            const payload = { question_bank_id, selected_answer_key };
            if (pinPos && typeof pinPos.x === "number" && typeof pinPos.y === "number") {
              payload.pin_position = { x: pinPos.x, y: pinPos.y };
            }
            return payload;
          });
          submitAttemptCall(currentAttempt.id, answerList).then((res) => {
            const data = res?.data;
            if (res?.status === 200 && data) {
              setResult(data);
              setResultDialog(true);
              setCurrentAttempt(null);
              setTimeLeft(null);
              loadPastAttempts();
            }
            setLoading(false);
          });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [currentAttempt?.id, timeLeft, loadPastAttempts]);

  const handleSelectAnswer = (questionBankId, key) => {
    setAnswers((prev) => ({ ...prev, [questionBankId]: key }));
  };

  const handleSetPinPosition = (questionBankId, x, y) => {
    setPinPositions((prev) => ({ ...prev, [questionBankId]: { x, y } }));
  };

  const viewResultDetails = async (attemptId) => {
    const res = await getAttemptResultCall(attemptId);
    const data = res?.data;
    if (data) {
      setResultDetails(data);
      setResultDialog(true);
    }
  };

  const resetToCodeEntry = () => {
    setAttemptPreview(null);
    setCurrentAttempt(null);
    setAccessCode("");
    setError("");
    setResult(null);
    setResultDetails(null);
    setResultDialog(false);
  };

  const questions = currentAttempt?.questions || [];
  const current = currentAttempt ? questions[currentQuestionIndex] : null;

  useEffect(() => {
    if (!currentAttempt || !current || current.question_type !== "slide_referenced") {
      if (slideMapRef.current) {
        slideMapRef.current.remove();
        slideMapRef.current = null;
      }
      slideAnswerMarkerRef.current = null;
      return;
    }

    const is3D = looksLike3DUrl(current.question_image_url);
    if (is3D) {
      if (slideMapRef.current) {
        slideMapRef.current.remove();
        slideMapRef.current = null;
      }
      slideAnswerMarkerRef.current = null;
      return;
    }

    const containerId = "student-quiz-slide-map";
    const tileUrl = current.question_image_url;
    if (!tileUrl) return;

    const t = setTimeout(() => {
      const el = document.getElementById(containerId);
      if (!el) return;

      if (slideMapRef.current) {
        slideMapRef.current.remove();
        slideMapRef.current = null;
      }

      const map = L.map(containerId, {
        maxZoom: 10,
        maxBounds: L.latLngBounds(L.latLng(-85, -180), L.latLng(85, 180)),
        maxBoundsViscosity: 1,
      }).setView([-10.505, -0.09], 2);

      L.tileLayer(tileUrl, {
        attribution: "&copy; Microlab",
        maxZoom: 10,
        noWrap: true,
        bounds: L.latLngBounds(L.latLng(-85, -180), L.latLng(85, 180)),
      }).addTo(map);

      const pinsList = !current.pins
        ? []
        : typeof current.pins === "string"
          ? (() => {
              try {
                return JSON.parse(current.pins);
              } catch {
                return [];
              }
            })()
          : Array.isArray(current.pins)
            ? current.pins
            : [];
      pinsList.forEach((feature) => {
        const coords = feature?.geometry?.coordinates;
        const label = feature?.properties?.label ?? "?";
        if (coords && coords.length >= 2) {
          const [lng, lat] = coords;
          L.marker([lat, lng], { icon: createSlidePinIcon(label) }).addTo(map);
        }
      });
      if (pinsList.length >= 2) {
        const bounds = L.latLngBounds(
          pinsList.map((f) => {
            const [lng, lat] = f?.geometry?.coordinates || [0, 0];
            return [lat, lng];
          })
        );
        if (bounds.isValid()) map.fitBounds(bounds, { padding: [20, 20], maxZoom: 10 });
      }

      slideMapRef.current = map;
    }, 150);

    return () => {
      clearTimeout(t);
      if (slideMapRef.current) {
        slideMapRef.current.remove();
        slideMapRef.current = null;
      }
      slideAnswerMarkerRef.current = null;
    };
  }, [currentAttempt?.id, currentQuestionIndex, current?.question_bank_id, current?.question_image_url, current?.question_type, current?.pins]);

  if (currentAttempt) {
    const total = questions.length;
    const answeredIds = new Set();
    questions.forEach((q) => {
      const id = q.question_bank_id;
      if (id == null || id === "") return;
      const hasAnswer = answers[id] != null && String(answers[id]).trim() !== "";
      const hasPin = q.question_type === "slide_referenced" && pinPositions[id] != null;
      if (hasAnswer || hasPin) answeredIds.add(id);
    });
    const answeredCount = answeredIds.size;
    const progress = total ? (answeredCount / total) * 100 : 0;
    const progressValue = Number.isFinite(progress) ? Math.min(100, Math.max(0, progress)) : 0;

    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3}>
          <Card sx={{ p: 2 }}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={2}>
              <MDTypography variant="h6">{currentAttempt.quiz_title || "Quiz"}</MDTypography>
              <MDTypography variant="button" color={timeLeft <= 60 ? "error" : "text"}>
                Time left: {formatTime(timeLeft)}
              </MDTypography>
            </MDBox>
            <MDBox
              sx={{
                mb: 2,
                height: 8,
                borderRadius: 1,
                bgcolor: "grey.300",
                overflow: "hidden",
              }}
            >
              <MDBox
                sx={{
                  height: "100%",
                  width: `${progressValue}%`,
                  bgcolor: "info.main",
                  borderRadius: 1,
                  transition: "width 0.3s ease",
                }}
              />
            </MDBox>
            <MDTypography variant="caption">Question {currentQuestionIndex + 1} of {total}</MDTypography>

            {current && (
              <MDBox mt={2}>
                <MDBox
                  sx={{
                    mb: 2,
                    "& img": { maxWidth: "100%", height: "auto" },
                  }}
                  dangerouslySetInnerHTML={{ __html: current.question_content || "" }}
                />

                {current.question_type === "slide_referenced" && current.question_image_url ? (
                  (() => {
                    const is3D = looksLike3DUrl(current.question_image_url);
                    if (is3D) {
                      return (
                        <MDBox mb={2}>
                          <model-viewer
                            src={current.question_image_url}
                            alt="3D Model"
                            camera-controls
                            auto-rotate
                            style={{ width: "100%", height: 380 }}
                          />
                        </MDBox>
                      );
                    }
                    return (
                      <MDBox
                        mb={2}
                        sx={{
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          overflow: "hidden",
                          height: 380,
                          minHeight: 380,
                        }}
                      >
                        <div
                          id="student-quiz-slide-map"
                          style={{ width: "100%", height: 380, minHeight: 380 }}
                        />
                      </MDBox>
                    );
                  })()
                ) : current.question_type !== "slide_referenced" && current.question_image_url ? (
                  <MDBox mb={2}>
                    <Box
                      component="img"
                      src={current.question_image_url}
                      alt="Question"
                      sx={{ maxWidth: "100%", maxHeight: 320, objectFit: "contain", borderRadius: 1 }}
                    />
                  </MDBox>
                ) : null}
                <FormControl component="fieldset" fullWidth>
                  <RadioGroup
                    value={answers[current.question_bank_id] || ""}
                    onChange={(e) => handleSelectAnswer(current.question_bank_id, e.target.value)}
                  >
                    {(current.answers || []).map((a) => (
                      <FormControlLabel
                        key={a.key}
                        value={a.key}
                        control={<Radio />}
                        label={
                          a.text ? (
                            <span dangerouslySetInnerHTML={{ __html: a.text }} />
                          ) : (
                            a.image_url && (
                              <Box component="img" src={a.image_url} alt="" sx={{ maxHeight: 80, ml: 0.5 }} />
                            )
                          )
                        }
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </MDBox>
            )}

            <MDBox mt={3} display="flex" justifyContent="space-between" flexWrap="wrap" gap={1}>
              <Button
                disabled={currentQuestionIndex === 0}
                onClick={() => setCurrentQuestionIndex((i) => i - 1)}
              >
                Previous
              </Button>
              {currentQuestionIndex < total - 1 ? (
                <Button variant="contained" onClick={() => setCurrentQuestionIndex((i) => i + 1)}>
                  Next
                </Button>
              ) : (
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={submitCurrentAttempt}
                  disabled={loading}
                >
                  {loading ? "Submitting..." : "Submit Quiz"}
                </MDButton>
              )}
            </MDBox>
          </Card>
        </MDBox>

        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDTypography variant="h4" fontWeight="medium" mb={3}>
          University Quiz
        </MDTypography>

        {!attemptPreview ? (
          <Card sx={{ p: 3, maxWidth: 480 }}>
            <MDTypography variant="body2" color="text" mb={2}>
              Enter the quiz code shared by your instructor to access a university quiz.
            </MDTypography>
            <TextField
              fullWidth
              label="Quiz code"
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
              placeholder="e.g. ABC123"
              inputProps={{ maxLength: 10 }}
              sx={{ mb: 2 }}
            />
            {error && (
              <MDTypography variant="caption" color="error" sx={{ display: "block", mb: 1 }}>
                {error}
              </MDTypography>
            )}
            <MDButton variant="gradient" color="info" onClick={handleAccessQuiz} disabled={loading}>
              {loading ? "Loading..." : "Access Quiz"}
            </MDButton>
          </Card>
        ) : (
          <Card sx={{ p: 3, maxWidth: 560 }}>
            <MDTypography variant="h6" mb={1}>{attemptPreview.quiz_title || "Quiz"}</MDTypography>
            <MDBox mb={1}>
              <Chip size="small" label={STATUS_LABELS[attemptPreview.status] || "Active"} color="info" sx={{ mr: 1 }} />
              <MDTypography variant="caption" color="text">
                Time limit: {attemptPreview.time_limit_minutes || 30} minutes
              </MDTypography>
            </MDBox>
            <MDTypography variant="body2" color="text" mb={2}>
              {attemptPreview.questions?.length || 0} questions
            </MDTypography>
            <MDBox display="flex" gap={1}>
              <MDButton variant="gradient" color="info" onClick={startOrContinueQuiz}>
                {attemptPreview.status === "in_progress" ? "Continue Quiz" : "Start Quiz"}
              </MDButton>
              <Button variant="outlined" onClick={resetToCodeEntry}>Cancel</Button>
            </MDBox>
          </Card>
        )}

        {pastAttempts.filter((a) => a.status === "submitted").length > 0 && (
          <MDBox mt={4}>
            <MDTypography variant="h6" mb={2}>Past attempts</MDTypography>
            {pastAttempts.filter((a) => a.status === "submitted").slice(0, 10).map((a) => (
              <Card key={a.id} sx={{ p: 2, mb: 1 }}>
                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                  <MDTypography>{a.quizAssignment?.title || "Quiz"} — Score: {a.score}%</MDTypography>
                  <Button size="small" onClick={() => viewResultDetails(a.id)}>View result</Button>
                </MDBox>
              </Card>
            ))}
          </MDBox>
        )}

        <Dialog open={resultDialog} onClose={() => { setResultDialog(false); setResult(null); setResultDetails(null); }} maxWidth={resultDetails ? "md" : "sm"} fullWidth PaperProps={{ sx: { bgcolor: "background.card", color: "text.main" } }}>
          <DialogTitle>Quiz Result</DialogTitle>
          <DialogContent>
            {resultDetails ? (
              <>
                <MDTypography>Score: {resultDetails.score}% — Correct: {resultDetails.correct_count} / {resultDetails.total_questions}</MDTypography>
                {(resultDetails.questions || []).map((q, i) => (
                  <MDBox key={q.question_bank_id || i} mt={2} p={1.5} sx={{ bgcolor: "background.default", borderRadius: 1 }}>
                    <MDTypography variant="caption" color={q.is_correct ? "success" : "error"}>
                      {q.is_correct ? "Correct" : "Incorrect"}
                    </MDTypography>
                    <MDBox dangerouslySetInnerHTML={{ __html: q.question_content || "" }} sx={{ mt: 0.5 }} />
                    {q.explanation && <MDTypography variant="caption" color="text">Explanation: {q.explanation}</MDTypography>}
                  </MDBox>
                ))}
              </>
            ) : result && (
              <MDBox>
                <MDTypography>Score: {result.score}%</MDTypography>
                <MDTypography>Correct: {result.correct_count} / {result.total_questions}</MDTypography>
              </MDBox>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setResultDialog(false); setResult(null); setResultDetails(null); }}>Close</Button>
          </DialogActions>
        </Dialog>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
